import Badge from "@/components/ui/badge";
import { ProductItemInterface } from "@/assets/interfaces";
import { capitalizeFirstLetter } from "@/assets/js/utils/functions";
import { getDiscountPercentage, getFormattedDate } from "@/assets/js/utils/utils";
import { DiscountBadge } from "../../items/bages";
import classNames from "classnames";
import { useMemo } from "react";
import { isObject } from "formik";

interface ProductInfoProps {
  item: ProductItemInterface;
  formatAsCurrency: (price: number) => string;
}

const ProductInfo = ({ item, formatAsCurrency }: ProductInfoProps) => {
  const discountExists = item.discount_price !== undefined && item.discount_price > 0;

  const categories = useMemo(() => {
    const o = item.category ? [item.category] : [];
    o.push(...(item.tags ?? []).filter((t) => isObject(t) && t?.id !== item?.category?.id));
    return o;
  }, [item]);

  return (
    <div className="mb-4 space-y-3.5">
      <div className="flex items-start flex-col justify-between mb-2.5">
        <div className="border-b border-grey-divider w-full pb-3.75">
          <h5
            className={`text-black font-display font-bold text-2lg md:text-3lg max-w-full whitespace-nowrap overflow-ellipsis  ${
              item.available_to_purchase ? "text-opacity-100" : "text-opacity-50"
            }`}
          >
            {capitalizeFirstLetter(item.name)}
          </h5>
          <div
            className={classNames("text-1xs sm:text-sm text-grey-subtext", {
              "text-opacity-100": item.available,
              "text-opacity-70 font-medium": !item.available,
              "flex items-center": !discountExists,
            })}
          >
            {discountExists && (
              <div className="flex items-center flex-wrap">
                <div className={`text-black-secondary font-semibold text-base sm:text-lg font-display mb-2 mr-1.5`}>
                  {formatAsCurrency(item.discount_price)}
                </div>
                {item?.available && (
                  <DiscountBadge className="mb-2" percentage={getDiscountPercentage(item)} mode="dark" />
                )}
              </div>
            )}
            <div
              className={classNames({
                "line-through text-xs sm:text-1xs font-display": discountExists,
                "text-black-secondary font-semibold text-base sm:text-lg font-display": !discountExists,
              })}
            >
              {formatAsCurrency(item.price)}
            </div>
          </div>
        </div>
        {!item.available && <Badge text="Unavailable" color="red" />}
        <div className="flex w-full flex-col space-y-1.5 mt-3.75">
          <dd className="text-black-secondary text-1xs sm:text-sm whitespace-pre-line">{item?.description}</dd>
        </div>
        <div className="flex justify-between gap-2">
          {(item?.quantity || item.is_always_available) && item?.available && (
            <div className="flex mt-2">
              <Badge
                text={
                  item.is_always_available
                    ? "In Stock"
                    : item?.quantity > 0
                    ? `${item.quantity} in stock`
                    : "Out of stock"
                }
                // color={item?.quantity > 0 || item.is_always_available ? "green" : "red"}
                className={item?.quantity > 0 || item.is_always_available ? "text-primary-500" : "!text-accent-red-500"}
              />
            </div>
          )}

          {item?.expiry_date && !!new Date(item?.expiry_date) && (
            <div className="flex mt-2">
              <Badge text={`Best before ${getFormattedDate(new Date(item?.expiry_date))}`} color="primary" />
            </div>
          )}

          {(item?.category || item?.tags) && (
            <div className="flex mt-2">
              {categories.map((c) => (
                <Badge text={`${c?.emoji ? `${c?.emoji} ` : ""}${c?.name}`} color="primary" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
